import { useRef } from 'react';
import { useFramework, useScale } from '@/framework';
import type { ITouchKitEvent } from '@/utils/event-kit';
import { getLayerInfoWithRotate, isRectOutOfBounds, traverseLayers } from '@/utils/layer-kit';
import {
    MOVING_ABSORB_DISTANCE,
    REFERENCE_LINE_DIRECTION,
    REFERENCE_LINE_EVENT_NAMES,
    SMART_GUIDE_EVENT_NAMES,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import { useMouseOperateState } from '../../states/mouse-operate-state';
import BlockList from '../tools/block-list';
import LayerBlock from '../tools/layer-block';
import LineList from '../tools/line-list';
import { getCurrentOperateLayer } from '../tools/utils';

const outOfRange = (n: number, range: number[]) => {
    return n <= range[0] || n >= range[1];
};

export const useMovingProxy = () => {
    const framework = useFramework();
    const scale = useScale();

    const referenceLines = useMouseOperateState()((state) => state.referenceLines);

    const { width, height } = framework.stage.getActiveStageSize();
    const containerSize = { width, height };

    // x 轴吸附
    const isAdsorbX = useRef<boolean>(false);
    // x 轴吸附范围
    const adsorbRangeX = useRef<number[]>([0, 0]);
    // y 轴吸附
    const isAdsorbY = useRef<boolean>(false);
    // y 轴吸附范围
    const adsorbRangeY = useRef<number[]>([0, 0]);
    // 吸附状态下的位移
    const adsorbDisplacement = useRef<IPoint>({ x: 0, y: 0 });
    // 间距 x 轴吸附
    const isBlockAdsorbX = useRef<boolean>(false);
    // 间距 y 轴吸附
    const isBlockAdsorbY = useRef<boolean>(false);

    const lineData = useRef<LineList>();
    const blockData = useRef<BlockList>();
    const layerBlock = useRef<LayerBlock>();

    let layers: ILayerProps[] = [];

    const proxyBeforeMoving = () => {
        const layer = getCurrentOperateLayer(framework);
        if (!layer) return;

        lineData.current = new LineList();
        blockData.current = new BlockList();

        // 把参考线添加到吸附的元素计算列表中
        if (referenceLines.length) {
            const stageIndex = framework.stage.getActiveStageIndex();
            const top = framework.getStageTop(stageIndex);

            // 多画布的情况, 需要把参考线转换到当前画布的坐标系中
            const correctedReferenceLines = referenceLines.map((line) => {
                if (line.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                    return {
                        ...line,
                        position: line.position - top,
                    };
                }
                return line;
            });
            lineData.current.addReferenceLineData(correctedReferenceLines);
        }
        // 舞台边缘
        lineData.current.push({ width, height, left: 0, top: 0, rotate: 0, isStage: true, id: 'stage_' + Date.now() });
        // 元素
        if (layer.parentId) {
            const parentLayer = framework.getLayer(layer.parentId);
            if (parentLayer) {
                layers = (parentLayer.children || []).filter(({ id }) => id !== layer.id);
            }
        } else {
            const originIndex = framework.stage.getActiveOriginIndex();
            layers = framework.getLayers(originIndex);
        }

        // 过滤不在可视范围内的图层
        const stageSize = framework.stage.getActiveStageSize();
        layers = layers.filter((layer) => {
            const layerRect = getLayerInfoWithRotate(layer);
            const isOutOfBounds = isRectOutOfBounds(layerRect, stageSize);
            return !isOutOfBounds;
        });

        traverseLayers(
            layers,
            layer,
            (item: ILayerProps) => {
                lineData.current!.push(item);
                if (layer) {
                    blockData.current!.push(item, layers, layer, containerSize);
                }
            },
            false,
        );
    };
    const proxyMoving = (
        displacementX: number,
        displacementY: number,
        oldLayerRect: ILayerProps,
        e: ITouchKitEvent,
    ) => {
        const layer = getCurrentOperateLayer(framework);
        if (!layer) return { x: 0, y: 0 };

        const lineNearest = lineData.current?.getNearest(layer);
        layerBlock.current = new LayerBlock(layer.id);
        const blockNearest = layerBlock.current!.getNearest(layer, blockData.current, containerSize);

        // 转换吸附距离到图层
        const activeLayerInfo = getLayerInfoWithRotate(oldLayerRect);
        const adsorbDistance = Math.max(1, Math.ceil(MOVING_ABSORB_DISTANCE / scale));

        if (lineNearest || blockNearest) {
            if (lineNearest) {
                if (!isBlockAdsorbX.current) {
                    // x 轴吸附位移
                    const nearestDisplacementX = lineNearest.vertical.position - activeLayerInfo.left;
                    // 进入吸附范围
                    if (Math.abs(lineNearest.vertical.distance) < adsorbDistance) {
                        if (!isAdsorbX.current) {
                            adsorbDisplacement.current.x = nearestDisplacementX;
                            // 移动吸附范围
                            adsorbRangeX.current = [
                                nearestDisplacementX - adsorbDistance,
                                nearestDisplacementX + adsorbDistance,
                            ];
                            isAdsorbX.current = true;
                        }
                    }
                    if (isAdsorbX.current) {
                        // 超出吸附范围, 停止吸附
                        if (outOfRange(displacementX, adsorbRangeX.current)) {
                            isAdsorbX.current = false;
                            adsorbDisplacement.current.x = displacementX;
                        }
                    } else {
                        adsorbDisplacement.current.x = displacementX;
                    }
                }
                if (!isBlockAdsorbY.current) {
                    // y 轴吸附位移
                    const nearestDisplacementY = lineNearest.horizontal.position - activeLayerInfo.top;
                    // 进入吸附范围
                    if (Math.abs(lineNearest.horizontal.distance) < adsorbDistance) {
                        if (!isAdsorbY.current) {
                            adsorbDisplacement.current.y = nearestDisplacementY;
                            // 吸附范围
                            adsorbRangeY.current = [
                                nearestDisplacementY - adsorbDistance,
                                nearestDisplacementY + adsorbDistance,
                            ];
                            isAdsorbY.current = true;
                        }
                    }
                    if (isAdsorbY.current) {
                        // 超出吸附范围, 停止吸附
                        if (outOfRange(displacementY, adsorbRangeY.current)) {
                            isAdsorbY.current = false;
                            adsorbDisplacement.current.y = displacementY;
                        }
                    } else {
                        adsorbDisplacement.current.y = displacementY;
                    }
                }
            }
            if (blockNearest) {
                if (!isAdsorbX.current) {
                    // 计算 x 轴吸附
                    const nearestDisplacementX = blockNearest.vertical.position - activeLayerInfo.left;
                    if (Math.abs(blockNearest.vertical.distance) < adsorbDistance) {
                        if (!isBlockAdsorbX.current) {
                            adsorbDisplacement.current.x = nearestDisplacementX;
                            adsorbRangeX.current = [
                                nearestDisplacementX - adsorbDistance,
                                nearestDisplacementX + adsorbDistance,
                            ];
                            isBlockAdsorbX.current = true;
                        }
                    }
                    if (isBlockAdsorbX.current) {
                        if (outOfRange(displacementX, adsorbRangeX.current)) {
                            isBlockAdsorbX.current = false;
                            adsorbDisplacement.current.x = displacementX;
                        }
                    } else {
                        adsorbDisplacement.current.x = displacementX;
                    }
                }
                if (!isAdsorbY.current) {
                    // 计算 y 轴吸附
                    const nearestDisplacementY = blockNearest.horizontal.position - activeLayerInfo.top;
                    if (Math.abs(blockNearest.horizontal.distance) < adsorbDistance) {
                        if (!isBlockAdsorbY.current) {
                            adsorbDisplacement.current.y = nearestDisplacementY;
                            adsorbRangeY.current = [
                                nearestDisplacementY - adsorbDistance,
                                nearestDisplacementY + adsorbDistance,
                            ];
                            isBlockAdsorbY.current = true;
                        }
                    }
                    if (isBlockAdsorbY.current) {
                        if (outOfRange(displacementY, adsorbRangeY.current)) {
                            isBlockAdsorbY.current = false;
                            adsorbDisplacement.current.y = displacementY;
                        }
                    } else {
                        adsorbDisplacement.current.y = displacementY;
                    }
                }
            }
        } else {
            adsorbDisplacement.current.x = displacementX;
            adsorbDisplacement.current.y = displacementY;
        }

        return adsorbDisplacement.current;
    };

    const proxyAfterMoving = (e: ITouchKitEvent) => {
        const layer = getCurrentOperateLayer(framework);
        if (layer) {
            // 对齐 UI 展示
            const lines = lineData.current?.getLines(layer);
            if (lines) {
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_LINES, lines);
            }

            // 等间距 UI 展示
            layerBlock.current!.collectBlocks(layer, layers, layer, containerSize);
            const blocks = layerBlock.current!.getBlocks(blockData.current);
            if (blocks) {
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_BLOCKS, blocks);
            }

            // 高亮参考线
            const refLineIds = lineData.current?.getReferenceLines(layer);
            if (refLineIds) {
                framework.events.emit(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, refLineIds);
            }
        }

        // 清除临时数据
        if (e.isEnd) {
            lineData.current = undefined;
            blockData.current = undefined;
            isAdsorbX.current = false;
            isAdsorbY.current = false;
            isBlockAdsorbX.current = false;
            isBlockAdsorbY.current = false;
        }
    };

    return {
        proxyMoving,
        proxyBeforeMoving,
        proxyAfterMoving,
    };
};
